import React, { useState, useEffect } from "react";
import { Typography, Card, CardContent, Grid, Box, Tabs, Tab } from "@mui/material";
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { getCustomerTaxData } from "../../../services/customer.service";
import HomeIcon from '@mui/icons-material/Home';
import BusinessIcon from '@mui/icons-material/Business';
import { maskEINLast4 } from "../../../helper/einMask.helper";

export interface PersonalTaxesData {
  ein: string;
  taxes: number;
  repairs: number;
  interest: number;
  insurance: number;
  utilities: number;
  grossRents: number;
  properties: { name: string; type: string; address: string; fairRentalDays: number; personalUseDays: number }[];
  advertising: number;
  commissions: number;
  depreciation: number;
  incomeOrLoss: number;
  autoAndTravel: number;
  otherExpenses: { amount: number; description: string }[];
  totalExpenses: number;
  totalGrossRents: number;
  wagesAndSalaries: number;
  totalExpensesAmount: number;
  cleaningAndMaintenance: number;
  netGainLossFromForm4797: number;
  legalAndOtherProfessionalFees: number;
  netIncomeLossFromPartnerships: number;
  netRentalRealEstateIncomeLoss: number;
}

export interface BusinessTaxesData {
  ein: string;
  rents: number;
  balance: number;
  badDebts: number;
  interest: number;
  refunded: number;
  depletion: number;
  amountOwed: number;
  advertising: number;
  grossProfit: number;
  netGainLoss: number;
  overpayment: number;
  depreciation: number;
  costOfGoodsSold: number;
  otherDeductions: number;
  otherIncomeLoss: number;
  totalDeductions: number;
  totalIncomeLoss: number;
  salariesAndWages: number;
  taxFromScheduleD: number;
  taxesAndLicenses: number;
  estimatedTaxPenalty: number;
  grossReceiptsOrSales: number;
  repairsAndMaintenance: number;
  compensationOfOfficers: number;
  employeeBenefitPrograms: number;
  pensionAndProfitSharing: number;
  energyEfficientDeduction: number;
  lessReturnsAndAllowances: number;
  taxDepositedWithForm7004: number;
  ordinaryBusinessIncomeLoss: number;
  creditForFederalTaxPaidOnFuels: number;
  currentYearEstimatedTaxPayments: number;
  excessNetPassiveIncomeOrLifoRecaptureTax: number;
  electivePaymentElectionAmountFromForm3800: number;
}

export interface TaxDataResponse {
  id: string;
  year: number;
  personalTaxes: PersonalTaxesData;
  businessTaxes: BusinessTaxesData;
  docIdPersonalTaxes: string;
  docIdBusinessTaxes: string;
}

interface DSCDetailsProps {
  businessId: string;
  loan: any;
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const TaxField: React.FC<{ label: string; value: number; highlight?: boolean }> = ({ label, value, highlight }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", py: 1, borderBottom: "1px solid #e0e0e0" }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" fontWeight={highlight ? 700 : 500} color={highlight ? (value >= 0 ? "success.main" : "error.main") : "text.primary"}>
      {formatCurrency(value)}
    </Typography>
  </Box>
);

const DSCDetails: React.FC<DSCDetailsProps> = ({ businessId, loan }) => {
  const [taxDataList, setTaxDataList] = useState<TaxDataResponse[]>([]);
  const [rate, setRate] = useState();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeYear, setActiveYear] = useState<number>(0);

  useEffect(() => {
    const fetchTaxData = async () => {
      if (!businessId) return;
      try {
        setLoading(true);
        setError(null);
        const {taxData, rate} = await getCustomerTaxData(businessId);
        setRate(rate.rate || 0)
        if (Array.isArray(taxData)) {
          setTaxDataList(taxData);
          if (taxData.length > 0) {
            setActiveYear(0);
          }
        } else if (taxData) {
          setTaxDataList([taxData]);
        } else {
          setTaxDataList([]);
        }
      } catch (err) {
        console.error("Error fetching tax data:", err);
        setError("Failed to load DSC details");
      } finally {
        setLoading(false);
      }
    };

    fetchTaxData();
  }, [businessId]);

  const handleYearChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveYear(newValue);
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography color="text.secondary">Loading DSC details...</Typography>
      </Box>
    );
  }

  if (error || taxDataList.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography color="text.secondary">No DSC details available</Typography>
      </Box>
    );
  }

  const currentTaxData = taxDataList[activeYear];
  const { personalTaxes, businessTaxes } = currentTaxData;
  const ebitda = (businessTaxes?.rents || 0) + (businessTaxes?.ordinaryBusinessIncomeLoss || 0);
  const personalEbitda = (personalTaxes?.incomeOrLoss || 0) + (personalTaxes?.interest || 0) + (personalTaxes?.depreciation || 0) + (personalTaxes?.otherExpenses?.reduce((sum, exp) => sum + exp.amount, 0) || 0);
  const months = 25 * 12;
  const pv = parseFloat(loan.loanSize) || 0;
  const r = loan.loanType === 'SBA' ? (rate || 0) + 2 : loan.loanType === 'conventional' ? (rate || 0) + 3.25 : 0
  const ra = (r / 100) / 12;
  const PMT = (ra * pv) / (1 - Math.pow(1 + ra, -months)); 
  const annualPayment = (PMT || 0) * 12;
  const totalEbitda = ebitda + personalEbitda;
  const dsc = annualPayment > 0 ? totalEbitda / annualPayment : 0;
  
  
  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}
      >
        {taxDataList.length > 1 && (
          <Tabs value={activeYear} onChange={handleYearChange} sx={{ ml: 2 }}>
            {taxDataList.map((data, index) => (
              <Tab
                key={data.id || index}
                label={
                  data.year ? `Tax Year ${data.year}` : `Year ${index + 1}`
                }
              />
            ))}
          </Tabs>
        )}
      </Box>

      <Box
        sx={{
          mt: 2,
          mb: 2,
          p: 3,
          background: "linear-gradient(135deg, #f8f9ff 0%, #e8f0ff 100%)",
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        }}
      >
        <Typography variant="h6" fontWeight={700} sx={{ mb: 3, textAlign: 'center' }}>
          Debt Service Coverage Metrics
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'white', borderRadius: 2 }}>
              <AttachMoneyIcon fontSize="large" color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h6" fontWeight={700}>
                {formatCurrency(PMT)}
              </Typography>
              <Typography variant="body2" color="text.secondary">Monthly Debt</Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'white', borderRadius: 2 }}>
              <CalendarTodayIcon fontSize="large" color="secondary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h6" fontWeight={700}>
                {formatCurrency(annualPayment)}
              </Typography>
              <Typography variant="body2" color="text.secondary">Annual Payment</Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'white', borderRadius: 2 }}>
              <TrendingUpIcon fontSize="large" sx={{ fontSize: 40, mb: 1 }} color={dsc >= 1 ? "success" : "error"} />
              <Typography variant="h6" fontWeight={700} color={dsc >= 1 ? "success.main" : "error.main"}>
                {dsc.toFixed(2)}x
              </Typography>
              <Typography variant="body2" color="text.secondary">DSC Ratio</Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }} style={{ width: "48%" }}>
          <Card
            sx={{
              borderRadius: 3,
              boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
              width: "100%",
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                fontWeight={600}
                color="primary"
                gutterBottom
              >
                Personal Taxes
              </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                EIN: {maskEINLast4(personalTaxes?.ein) }
              </Typography>

              {/* EBITDA Display - Personal Taxes */}
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  bgcolor: "action.hover",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography
                  variant="h5"
                  fontWeight={700}
                  color={personalEbitda >= 0 ? "success.main" : "error.main"}
                  sx={{ mb: 1 }}
                >
                  EBITDA: {formatCurrency(personalEbitda)}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    flexWrap: "wrap",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    <HomeIcon
                      sx={{ fontSize: 18, mr: 0.5, verticalAlign: "middle" }}
                    />{" "}
                    Gross Rents:{" "}
                    <span style={{ fontWeight: 600, color: "primary.main" }}>
                      {formatCurrency(personalTaxes?.grossRents || 0)}
                    </span>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <BusinessIcon
                      sx={{ fontSize: 18, mr: 0.5, verticalAlign: "middle" }}
                    />{" "}
                    Net Income/Loss:{" "}
                    <span style={{ fontWeight: 600, color: "secondary.main" }}>
                      {formatCurrency(personalTaxes?.incomeOrLoss || 0)}
                    </span>
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ mt: 2 }}>
                <TaxField
                  label="Gross Rents"
                  value={personalTaxes?.grossRents || 0}
                />
                <TaxField
                  label="Advertising"
                  value={personalTaxes?.advertising || 0}
                />
                <TaxField
                  label="Auto & Travel"
                  value={personalTaxes?.autoAndTravel || 0}
                />
                <TaxField
                  label="Cleaning & Maintenance"
                  value={personalTaxes?.cleaningAndMaintenance || 0}
                />
                <TaxField
                  label="Commissions"
                  value={personalTaxes?.commissions || 0}
                />
                <TaxField
                  label="Insurance"
                  value={personalTaxes?.insurance || 0}
                />
                <TaxField
                  label="Legal & Professional Fees"
                  value={personalTaxes?.legalAndOtherProfessionalFees || 0}
                />
                <TaxField
                  label="Interest"
                  value={personalTaxes?.interest || 0}
                />
                <TaxField label="Repairs" value={personalTaxes?.repairs || 0} />
                <TaxField label="Taxes" value={personalTaxes?.taxes || 0} />
                <TaxField
                  label="Utilities"
                  value={personalTaxes?.utilities || 0}
                />
                <TaxField
                  label="Wages & Salaries"
                  value={personalTaxes?.wagesAndSalaries || 0}
                />
                <TaxField
                  label="Depreciation"
                  value={personalTaxes?.depreciation || 0}
                />
                <TaxField
                  label="Total Expenses"
                  value={personalTaxes?.totalExpenses || 0}
                  highlight
                />
                <TaxField
                  label="Total Expenses Amount"
                  value={personalTaxes?.totalExpensesAmount || 0}
                />
                <TaxField
                  label="Total Gross Rents"
                  value={personalTaxes?.totalGrossRents || 0}
                />
                <TaxField
                  label="Net Gain/Loss From Form 4797"
                  value={personalTaxes?.netGainLossFromForm4797 || 0}
                />
                <TaxField
                  label="Net Income/Loss From Partnerships"
                  value={personalTaxes?.netIncomeLossFromPartnerships || 0}
                />
                <TaxField
                  label="Net Rental/Real Estate Income/Loss"
                  value={personalTaxes?.netRentalRealEstateIncomeLoss || 0}
                />
                <TaxField
                  label="Net Income/Loss"
                  value={personalTaxes?.incomeOrLoss || 0}
                  highlight
                />
              </Box>

              {personalTaxes?.otherExpenses &&
                personalTaxes.otherExpenses.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      gutterBottom
                    >
                      Other Expenses
                    </Typography>
                    {personalTaxes.otherExpenses.map((expense, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          py: 0.5,
                        }}
                      >
                        <Typography variant="body2">
                          {expense.description}
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {formatCurrency(expense.amount)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }} style={{ width: "48%" }}>
          <Card
            sx={{ borderRadius: 3, boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}
          >
            <CardContent>
              <Typography
                variant="h6"
                fontWeight={600}
                color="secondary"
                gutterBottom
              >
                Business Taxes
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                EIN: {maskEINLast4(businessTaxes?.ein) }
              </Typography>

              {/* EBITDA Display - Top of Business Taxes (similar to Personal layout) */}
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  bgcolor: "action.hover",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography
                  variant="h5"
                  fontWeight={700}
                  color={ebitda >= 0 ? "success.main" : "error.main"}
                  sx={{ mb: 1 }}
                >
                  EBITDA: {formatCurrency(ebitda)}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    flexWrap: "wrap",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    <HomeIcon
                      sx={{ fontSize: 18, mr: 0.5, verticalAlign: "middle" }}
                    />{" "}
                    Rents:{" "}
                    <span style={{ fontWeight: 600, color: "primary.main" }}>
                      {formatCurrency(businessTaxes?.rents || 0)}
                    </span>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <BusinessIcon
                      sx={{ fontSize: 18, mr: 0.5, verticalAlign: "middle" }}
                    />{" "}
                    Ordinary Business Income/Loss:{" "}
                    <span style={{ fontWeight: 600, color: "secondary.main" }}>
                      {formatCurrency(
                        businessTaxes?.ordinaryBusinessIncomeLoss || 0,
                      )}
                    </span>
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ mt: 2 }}>
                <TaxField
                  label="Gross Receipts/Sales"
                  value={businessTaxes?.grossReceiptsOrSales || 0}
                />
                <TaxField
                  label="Less Returns & Allowances"
                  value={businessTaxes?.lessReturnsAndAllowances || 0}
                />
                <TaxField label="Balance" value={businessTaxes?.balance || 0} />
                <TaxField
                  label="Cost of Goods Sold"
                  value={businessTaxes?.costOfGoodsSold || 0}
                />
                <TaxField
                  label="Gross Profit"
                  value={businessTaxes?.grossProfit || 0}
                />
                <TaxField
                  label="Net Gain/Loss"
                  value={businessTaxes?.netGainLoss || 0}
                />
                <TaxField
                  label="Other Income/Loss"
                  value={businessTaxes?.otherIncomeLoss || 0}
                />
                <TaxField
                  label="Total Income/Loss"
                  value={businessTaxes?.totalIncomeLoss || 0}
                  highlight
                />
                <TaxField
                  label="Compensation of Officers"
                  value={businessTaxes?.compensationOfOfficers || 0}
                />
                <TaxField
                  label="Salaries & Wages"
                  value={businessTaxes?.salariesAndWages || 0}
                />
                <TaxField
                  label="Repairs & Maintenance"
                  value={businessTaxes?.repairsAndMaintenance || 0}
                />
                <TaxField
                  label="Bad Debts"
                  value={businessTaxes?.badDebts || 0}
                />
                <TaxField label="Rents" value={businessTaxes?.rents || 0} />
                <TaxField
                  label="Taxes & Licenses"
                  value={businessTaxes?.taxesAndLicenses || 0}
                />
                <TaxField
                  label="Interest"
                  value={businessTaxes?.interest || 0}
                />
                <TaxField
                  label="Depreciation"
                  value={businessTaxes?.depreciation || 0}
                />
                <TaxField
                  label="Depletion"
                  value={businessTaxes?.depletion || 0}
                />
                <TaxField
                  label="Advertising"
                  value={businessTaxes?.advertising || 0}
                />
                <TaxField
                  label="Pension & Profit Sharing"
                  value={businessTaxes?.pensionAndProfitSharing || 0}
                />
                <TaxField
                  label="Employee Benefit Programs"
                  value={businessTaxes?.employeeBenefitPrograms || 0}
                />
                <TaxField
                  label="Energy Efficient Deduction"
                  value={businessTaxes?.energyEfficientDeduction || 0}
                />
                <TaxField
                  label="Other Deductions"
                  value={businessTaxes?.otherDeductions || 0}
                />
                <TaxField
                  label="Total Deductions"
                  value={businessTaxes?.totalDeductions || 0}
                  highlight
                />
                <TaxField
                  label="Ordinary Business Income/Loss"
                  value={businessTaxes?.ordinaryBusinessIncomeLoss || 0}
                  highlight
                />

                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  gutterBottom
                  sx={{ mt: 2 }}
                >
                  Tax Liability & Payments
                </Typography>
                <TaxField
                  label="Tax from Schedule D"
                  value={businessTaxes?.taxFromScheduleD || 0}
                />
                <TaxField
                  label="Current Year Estimated Tax Payments"
                  value={businessTaxes?.currentYearEstimatedTaxPayments || 0}
                />
                <TaxField
                  label="Tax Deposited with Form 7004"
                  value={businessTaxes?.taxDepositedWithForm7004 || 0}
                />
                <TaxField
                  label="Credit for Federal Tax Paid on Fuels"
                  value={businessTaxes?.creditForFederalTaxPaidOnFuels || 0}
                />
                <TaxField
                  label="Elective Payment Election Amount (Form 3800)"
                  value={
                    businessTaxes?.electivePaymentElectionAmountFromForm3800 ||
                    0
                  }
                />
                <TaxField
                  label="Excess Net Passive Income/LIFO Recapture Tax"
                  value={
                    businessTaxes?.excessNetPassiveIncomeOrLifoRecaptureTax || 0
                  }
                />
                <TaxField
                  label="Estimated Tax Penalty"
                  value={businessTaxes?.estimatedTaxPenalty || 0}
                />
                <TaxField
                  label="Overpayment"
                  value={businessTaxes?.overpayment || 0}
                />
                <TaxField
                  label="Refunded"
                  value={businessTaxes?.refunded || 0}
                />
                <TaxField
                  label="Amount Owed"
                  value={businessTaxes?.amountOwed || 0}
                  highlight
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DSCDetails;

